/**
 * iter-372 — Description ↔ Annexure parser/serializer.
 *
 * The AI emits descriptions in a 6-section Markdown format:
 *
 *   • **Scope of Work:**
 *   - <line>
 *
 *   • **Material Specification:**
 *   - <spec>
 *   - <spec>
 *
 *   • **Installation / Labour Details:**
 *   - <line>
 *
 *   • **Inclusions:**
 *   - <line>
 *
 *   • **Exclusions:**
 *   - <line>
 *
 *   • **Assumptions:**
 *   - <line>
 *
 * This module gives us two-way sync between the raw description string
 * (what Save persists) and the structured Annexure grid the user edits.
 *
 * parseDescription(str)  → { scope, material, installation, inclusions, exclusions, assumptions }
 * serializeDescription(obj) → canonical description string, preserving the • / - bullet grammar.
 */

const SECTION_ORDER = [
  { key: 'scope',        title: 'Scope of Work' },
  { key: 'material',     title: 'Material Specification' },
  { key: 'installation', title: 'Installation / Labour Details' },
  { key: 'inclusions',   title: 'Inclusions' },
  { key: 'exclusions',   title: 'Exclusions' },
  { key: 'assumptions',  title: 'Assumptions' },
];

// Aliases we accept when the LLM drifts on the heading wording.
const TITLE_ALIASES = {
  'scope of work':                    'scope',
  'scope':                            'scope',
  'material specification':           'material',
  'material specifications':          'material',
  'materials':                        'material',
  'installation':                     'installation',
  'installation / labour details':    'installation',
  'installation and labour':          'installation',
  'installation/labour details':      'installation',
  'labour details':                   'installation',
  'inclusions':                       'inclusions',
  'includes':                         'inclusions',
  'exclusions':                       'exclusions',
  'excludes':                         'exclusions',
  'assumptions':                      'assumptions',
  'assumption':                       'assumptions',
};

function emptyAnnexure() {
  return { scope: '', material: '', installation: '', inclusions: '', exclusions: '', assumptions: '' };
}

export function parseDescription(desc) {
  const out = emptyAnnexure();
  if (!desc || typeof desc !== 'string') return out;
  const lines = desc.split(/\r?\n/);
  let currentKey = null;
  let matchedAnyHeading = false;
  for (const raw of lines) {
    const line = raw.replace(/\u00A0/g, ' ').trimEnd();
    if (!line.trim()) continue;
    // Match section heading — supports "• **Title:**", "**Title:**", "Title:"
    const headingMatch = line.match(/^\s*(?:•\s*)?\*?\*?([^*:]+?)\*?\*?\s*:\s*\*?\*?\s*$/);
    if (headingMatch) {
      const normalized = headingMatch[1].trim().toLowerCase();
      const key = TITLE_ALIASES[normalized];
      if (key) { currentKey = key; matchedAnyHeading = true; continue; }
    }
    if (!currentKey) continue;
    // Bullet line — strip leading "- " if present, keep the rest verbatim
    // (preserving any **bold** markdown emitted by the AI).
    const bullet = line.replace(/^\s*[-*•]\s+/, '').trim();
    if (!bullet) continue;
    out[currentKey] = out[currentKey] ? `${out[currentKey]}\n${bullet}` : bullet;
  }
  // iter-379 — fallback: if the description is legacy (no 6-section
  // headings at all) but has real content, surface the whole text in the
  // "Scope of Work" column so the Annexure grid isn't dead-empty on
  // public views. `hasHeadings` on the return lets consumers still
  // detect the legacy case for the "Rewrite Legacy" AI action.
  if (!matchedAnyHeading) {
    const raw = desc.trim();
    if (raw) out.scope = raw;
  }
  Object.defineProperty(out, 'hasHeadings', { value: matchedAnyHeading, enumerable: false });
  return out;
}

/**
 * Convert one field's multi-line text into a list of "- <bullet>" lines.
 * Empty lines are dropped. Existing "- " prefixes are respected/normalised.
 */
function toBulletBlock(txt) {
  if (!txt) return '';
  return txt
    .split(/\r?\n/)
    .map((l) => l.replace(/\u00A0/g, ' ').trim())
    .filter(Boolean)
    .map((l) => (l.startsWith('- ') ? l : `- ${l.replace(/^[-*•]\s*/, '')}`))
    .join('\n');
}

export function serializeDescription(annexure) {
  if (!annexure) return '';
  const parts = [];
  for (const { key, title } of SECTION_ORDER) {
    const val = annexure[key];
    const block = toBulletBlock(val);
    if (!block) continue;
    parts.push(`• **${title}:**\n${block}`);
  }
  return parts.join('\n\n');
}

/**
 * True if the two annexure objects have identical field values.
 * Used to skip re-rendering the description when the annexure edit was
 * a no-op (e.g., the user retyped the same characters).
 */
export function annexuresEqual(a, b) {
  if (!a || !b) return false;
  for (const { key } of SECTION_ORDER) {
    if ((a[key] || '') !== (b[key] || '')) return false;
  }
  return true;
}

export const ANNEXURE_SECTIONS = SECTION_ORDER;
